import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { StatusType } from '@treasury/domain/channel/mappings/security';
import { AccountService } from '@treasury/domain/channel/services/account';
import { TmButtonType } from '@treasury/presentation/components/tm-button.types';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import {
    AccountValidationResult,
    IdentityDialogContext,
    IdentityDialogStrategy,
} from '../identity-verification-dialog.types';

export class SecureToken implements IdentityDialogStrategy {
    constructor(private readonly context: IdentityDialogContext) {}

    public get password() {
        return this.passwordRef.value?.value || '';
    }

    public set password(value) {
        if (!this.passwordRef.value?.value) {
            return;
        }

        this.passwordRef.value.value = value;
    }

    private readonly passwordRef = createRef<HTMLInputElement>();

    private isValid = false;

    @InjectProperty()
    declare private accountService: AccountService;

    public async validateAccountPassword(password: string) {
        const result = await this.accountService.validateSecureToken(password);

        return {
            result,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            success: (result as any).status === StatusType.Allow,
        } as AccountValidationResult;
    }

    public renderContent() {
        const { verifying, securityMessage } = this.context;
        if (securityMessage?.status === StatusType.SecureTokenLocked) {
            return nothing;
        }

        return html`<div class="caption">
                We need to verify your identity for the security of the account. Please enter your
                security code followed by your 4-digit PIN (no-space)
            </div>
            <div class="input-wrapper">
                <input
                    ${ref(this.passwordRef)}
                    class="password"
                    type="password"
                    maxlength="10"
                    .disabled=${verifying}
                    @change=${(e: KeyboardEvent) => this.validateInput(e)}
                    @input=${(e: KeyboardEvent) => this.validateInput(e)}
                    autocomplete="off"
                    data-1p-ignore="true"
                />
            </div> `;
    }

    public renderActions() {
        const { disableClose, verifying, verify, cancel, securityMessage } = this.context;
        if (securityMessage?.status === 'SecureTokenLocked') {
            return html`<tm-button importance="secondary" @click=${cancel}>Close</tm-button>`;
        }
        const cancelButton = disableClose
            ? nothing
            : html`<tm-button .type="${TmButtonType.Clear}" @click=${cancel}>Cancel</tm-button>`;
        return html`<div name="actions">
            <tm-button importance="primary" .disabled=${verifying || !this.isValid} @click=${verify}
                >Verify</tm-button
            >
            ${cancelButton}
        </div>`;
    }

    private validateInput(event: KeyboardEvent) {
        if (!event.target || this.password.length < 1) {
            return;
        }

        const { key } = event;
        const inputValue = this.password.trim();
        const isValid = inputValue.length === 10;
        this.isValid = isValid;
        this.context.requestUpdate();

        if (key === 'Enter' && isValid) {
            this.context.verify();
        }
    }
}
